import {
  ArrowDown,
  ArrowRight,
  CalendarDays,
  ChevronDown,
  Clock3,
  Glasses,
  Instagram,
  Mail,
  MapPin,
  Menu,
  Phone,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { FurGrass } from "./FurGrass";

const images = {
  hero: "https://www.augenweide.de/wp-content/uploads/2024/03/brillen_augenweide_koeln.jpg",
  store: "https://www.augenweide.de/wp-content/uploads/2024/03/kontakt-augenweide-optik-gmbh.jpg",
  sun: "https://www.augenweide.de/wp-content/uploads/2024/03/Sonnenbrillen-4.jpg",
  kids: "https://www.augenweide.de/wp-content/uploads/2024/03/kinderbrillen.jpg",
  goetti: "https://www.augenweide.de/wp-content/uploads/2026/01/DSC_0532-2048x954.jpg",
  logo: "https://www.augenweide.de/wp-content/uploads/2024/03/logo_augenweide.png",
};

const brands = [
  "Götti", "MYKITA", "Moscot", "Cutler and Gross", "theo", "REIZ", "Coblens",
  "Lunor", "Lesca", "Suzy Glam", "Oscar Magnuson", "Ørgreen", "Miga Studio", "ic! berlin",
];

const services = [
  {
    title: "Designerbrillen",
    text: "Handverlesene Kollektionen statt beliebiger Massenware. Wir finden die Fassung, die zu Gesicht, Alltag und Stil passt.",
    image: images.hero,
    link: "https://www.augenweide.de/brillen/",
  },
  {
    title: "Sonnenbrillen",
    text: "Aktuelle Modelle, auf Wunsch mit Sehstärke und Gläsern, die Komfort, Schutz und Charakter verbinden.",
    image: images.sun,
    link: "https://www.augenweide.de/sonnenbrillen/",
  },
  {
    title: "Kinderbrillen",
    text: "Brillen, die gut sitzen, viel mitmachen und von Kindern gern getragen werden. Sorgfältig angepasst und alltagstauglich.",
    image: images.kids,
    link: "https://www.augenweide.de/kinderbrillen/",
  },
];

type OverlayApi = { open?: () => void; ask?: (query: string) => void };

function openOverlay(query?: string) {
  const api = (window as unknown as { __overlay?: OverlayApi }).__overlay;
  if (query && api?.ask) return api.ask(query);
  if (api?.open) return api.open();
  document.querySelector("#besuch")?.scrollIntoView({ behavior: "smooth" });
}

function Header() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className={`site-header ${solid || open ? "is-solid" : ""}`}>
        <a className="brand" href="#top" aria-label="Augenweide Startseite">
          <img src={images.logo} alt="Augenweide Optik" />
        </a>
        <nav className="desktop-nav" aria-label="Hauptnavigation">
          <a href="#auswahl">Auswahl</a>
          <a href="#beratung">Beratung</a>
          <a href="#goetti">Götti Brand Store</a>
          <a href="#besuch">Besuch</a>
          <button className="nav-ai" type="button" onClick={() => openOverlay()}>
            <Sparkles size={15} /> Fragen Sie uns
          </button>
        </nav>
        <button className="menu-button" type="button" aria-label={open ? "Menü schließen" : "Menü öffnen"} onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </header>
      {open && (
        <nav className="mobile-nav" aria-label="Mobile Navigation">
          {["auswahl", "beratung", "goetti", "besuch"].map((id) => (
            <a key={id} href={`#${id}`} onClick={() => setOpen(false)}>
              {{ auswahl: "Auswahl", beratung: "Beratung", goetti: "Götti Brand Store", besuch: "Besuch" }[id]}
            </a>
          ))}
          <button type="button" onClick={() => { setOpen(false); openOverlay(); }}>
            <Sparkles size={18} /> Fragen Sie Augenweide
          </button>
        </nav>
      )}
    </>
  );
}

export function App() {
  const [formSent, setFormSent] = useState(false);

  return (
    <>
      <Header />
      <main id="top">
        <section className="hero">
          <img className="hero-image" src={images.hero} alt="Ausgewählte Designerbrille bei Augenweide Optik in Köln" fetchPriority="high" />
          <div className="hero-shade" />
          <div className="hero-content">
            <h1>Mehr als eine Marke.<br />Ihre Brille.</h1>
            <p>Unabhängige Augenoptik in Köln. Handverlesene Designerbrillen, präzise Sehanalyse und persönliche Beratung seit über 30 Jahren.</p>
            <div className="hero-actions">
              <a className="button button-light" href="#besuch"><CalendarDays size={18} /> Besuch planen</a>
              <button className="button button-glass" type="button" onClick={() => openOverlay("Welche Brille passt zu mir?")}>
                <Sparkles size={18} /> Digital beraten lassen
              </button>
            </div>
          </div>
          <a className="scroll-hint" href="#auswahl" aria-label="Zur Auswahl scrollen"><ArrowDown /></a>
        </section>

        <section className="intro section" id="auswahl">
          <div className="section-copy">
            <h2>Die ganze Welt guter Brillen.</h2>
            <p>Götti ist ein besonderer Teil der Augenweide. Aber längst nicht der einzige. Bei uns finden Sie internationale Designmarken, unabhängige Manufakturen und Lösungen für jeden Sehbedarf.</p>
          </div>
          <div className="numbers" aria-label="Augenweide auf einen Blick">
            <div><strong>30+</strong><span>Jahre Erfahrung</span></div>
            <div><strong>14+</strong><span>ausgewählte Marken</span></div>
            <div><strong>1:1</strong><span>persönliche Beratung</span></div>
          </div>
          <div className="brand-cloud" aria-label="Auswahl geführter Marken">
            {brands.map((brand) => <span key={brand}>{brand}</span>)}
          </div>
        </section>

        <section className="service-grid section" aria-label="Sortiment und Leistungen">
          {services.map((service) => (
            <a className="service-card" href={service.link} key={service.title} target="_blank" rel="noreferrer">
              <img src={service.image} alt={`${service.title} bei Augenweide Optik Köln`} loading="lazy" />
              <div className="service-card-content">
                <h3>{service.title}</h3>
                <p>{service.text}</p>
                <span>Mehr erfahren <ArrowRight size={18} /></span>
              </div>
            </a>
          ))}
        </section>

        <section className="advice section" id="beratung">
          <div className="advice-image-wrap">
            <img src={images.store} alt="Innenraum der Augenweide Optik am Hohenstaufenring in Köln" loading="lazy" />
            <div className="floating-note"><Glasses size={22} /><span>Fassung, Gläser und Passform aus einer Hand</span></div>
          </div>
          <div className="advice-copy">
            <h2>Gutes Sehen beginnt mit Zuhören.</h2>
            <p>Wir nehmen uns Zeit für Ihre Sehgewohnheiten, Ihren Stil und Ihren Alltag. Daraus entsteht eine Brille, die nicht nur gut aussieht, sondern jeden Tag richtig funktioniert.</p>
            <ul>
              <li>Präzise Sehanalyse und Brillenglasbestimmung</li>
              <li>Individuelle Stil- und Fassungsberatung</li>
              <li>Kontaktlinsen für Alltag, Sport und besondere Anlässe</li>
              <li>Eigene Werkstatt und sorgfältige Anpassung</li>
            </ul>
            <a className="text-link" href="tel:+49221231728">Persönlich beraten lassen <ArrowRight size={18} /></a>
          </div>
        </section>

        <section className="goetti" id="goetti">
          <img src={images.goetti} alt="Götti Designerbrille im Götti Brand Store Köln" loading="lazy" />
          <div className="goetti-content">
            <h2>Deutschlands erster Götti Brand Store. Mitten in der Augenweide.</h2>
            <p>Entdecken Sie die komplette Götti Kollektion mit Titan, Acetat, 3D-Druck, Sonnen- und Kinderbrillen. Exklusiv, umfassend und eingebettet in unsere markenunabhängige Beratung.</p>
            <button type="button" className="button button-dark" onClick={() => openOverlay("Was macht den Götti Brand Store in der Augenweide besonders?")}>
              Götti entdecken <ArrowRight size={18} />
            </button>
          </div>
        </section>

        <section className="visit section" id="besuch">
          <div className="visit-title">
            <h2>Sehen wir uns?</h2>
            <p>Am Hohenstaufenring, wenige Minuten vom Rudolfplatz. Kommen Sie spontan vorbei oder rufen Sie kurz an.</p>
          </div>
          <div className="visit-cards">
            <a href="https://maps.app.goo.gl/3ybitg4gYACTVhDVA" target="_blank" rel="noreferrer">
              <MapPin /><strong>Hohenstaufenring 72</strong><span>50674 Köln</span><small>Route öffnen <ArrowRight size={15} /></small>
            </a>
            <a href="tel:+49221231728">
              <Phone /><strong>0221 231728</strong><span>Persönlich erreichbar</span><small>Jetzt anrufen <ArrowRight size={15} /></small>
            </a>
            <div>
              <Clock3 /><strong>Di bis Fr</strong><span>10:00 bis 18:30 Uhr<br />Sa 10:00 bis 18:00 Uhr<br />Montag geschlossen</span>
            </div>
            <a href="mailto:mail@augenweide.de">
              <Mail /><strong>mail@augenweide.de</strong><span>Schreiben Sie uns</span><small>E-Mail öffnen <ArrowRight size={15} /></small>
            </a>
          </div>

          <div className="contact-panel" id="kontakt">
            <div className="contact-intro">
              <h2>Was dürfen wir für Sie tun?</h2>
              <p>Erzählen Sie uns kurz, worum es geht. Für eine verbindliche Anfrage erreichen Sie uns direkt per Telefon oder E-Mail.</p>
              <a href="tel:+49221231728"><Phone size={18} /> 0221 231728</a>
              <a href="mailto:mail@augenweide.de"><Mail size={18} /> mail@augenweide.de</a>
            </div>

            {formSent ? (
              <div className="contact-success" role="status">
                <Mail size={30} />
                <h3>Danke für Ihre Nachricht.</h3>
                <p>Dies ist ein Demoformular. Ihre Angaben wurden nicht übertragen oder gespeichert.</p>
                <button type="button" onClick={() => setFormSent(false)}>Neue Nachricht schreiben</button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={(event) => { event.preventDefault(); setFormSent(true); }}>
                <div className="form-row">
                  <label>
                    Name
                    <input type="text" name="name" autoComplete="name" placeholder="Ihr Name" required />
                  </label>
                  <label>
                    E-Mail
                    <input type="email" name="email" autoComplete="email" placeholder="name@beispiel.de" required />
                  </label>
                </div>
                <label>
                  Worum geht es?
                  <span className="select-field">
                    <select name="topic" defaultValue="">
                      <option value="" disabled>Bitte auswählen</option>
                      <option>Beratung und neue Brille</option>
                      <option>Sehtest</option>
                      <option>Kontaktlinsen</option>
                      <option>Service und Reparatur</option>
                      <option>Etwas anderes</option>
                    </select>
                    <ChevronDown size={18} aria-hidden="true" />
                  </span>
                </label>
                <label>
                  Nachricht
                  <textarea name="message" rows={5} placeholder="Wie können wir Ihnen helfen?" required />
                </label>
                <div className="form-submit">
                  <p>Demoformular: Es werden keine Daten übertragen.</p>
                  <button type="submit"><Mail size={18} /> Nachricht testen</button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <FurGrass />
        <div className="footer-veil" />
        <div className="footer-content">
          <div className="footer-lead">
            <div className="footer-mark">
              <img src="/assets/augenweide-signet.png" alt="Augenweide Optik Signet" />
            </div>
            <p>Unabhängige Augenoptik, handverlesene Designerbrillen und Deutschlands erster Götti Brand Store in Köln.</p>
            <button type="button" onClick={() => openOverlay("Wie kann Augenweide mir helfen?")}>
              <Sparkles size={17} /> Fragen Sie Augenweide
            </button>
          </div>
          <div className="footer-links">
            <div>
              <strong>Sortiment</strong>
              <a href="https://www.augenweide.de/brillen/">Brillen</a>
              <a href="https://www.augenweide.de/sonnenbrillen/">Sonnenbrillen</a>
              <a href="https://www.augenweide.de/kinderbrillen/">Kinderbrillen</a>
              <a href="https://www.augenweide.de/2018/03/08/kontaktlinsen/">Kontaktlinsen</a>
            </div>
            <div>
              <strong>Augenweide</strong>
              <a href="https://www.augenweide.de/kontakt/">Kontakt & Team</a>
              <a href="https://www.augenweide.de/aktuelle-markenbrillen-kollektionen/">Aktuelle Kollektionen</a>
              <a href="https://maps.app.goo.gl/3ybitg4gYACTVhDVA" target="_blank" rel="noreferrer">Route Google Maps</a>
            </div>
            <div>
              <strong>Besuch</strong>
              <span>Hohenstaufenring 72<br />50674 Köln</span>
              <span>Di bis Fr 10:00 bis 18:30<br />Sa 10:00 bis 18:00<br />Montag geschlossen</span>
            </div>
            <div>
              <strong>Kontakt</strong>
              <a href="tel:+49221231728">0221 231728</a>
              <a href="mailto:mail@augenweide.de">mail@augenweide.de</a>
              <a href="https://www.instagram.com/augenweideoptik" target="_blank" rel="noreferrer"><Instagram size={17} /> Instagram</a>
              <a href="https://www.facebook.com/AugenweideOptikKoeln" target="_blank" rel="noreferrer">Facebook</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© Augenweide Optik GmbH</span>
            <div>
              <a href="https://www.augenweide.de/impressum/">Impressum</a>
              <a href="https://www.augenweide.de/datenschutzerklaerung/">Datenschutz</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
